<script setup lang="ts">
import { Property, propertyName } from "~/classes/entity"

const props = defineProps({
  file: null
})
const { updateFileProperty, createPropertyWithDefaultValue } = useFileSystem()
/**
 * The list of options to show in our properties panel. This is supposed to be a list
 * of all possible properties that a user might see in the details pane, even though
 * a lot of these properties are non-editable. The idea is to make it clear which
 * properties are or are not able to be added/removed.
 *
 * TODO: is there a better place for this?
 */
const options = new Set([
  Property.FILE_NOTES,
  Property.FILE_RATING,
  Property.FILE_VIRTUAL_TAGS,
  Property.FILE_GENERATION_PROMPT,
  Property.FILE_GENERATION_NEGATIVE_PROMPT,
  Property.FILE_GENERATION_MODEL,
  Property.FILE_GENERATION_MODEL_VERSION,
  Property.FILE_GENERATION_SOURCE,
  Property.FILE_GENERATION_SEED,
])

function label(propertyId: Property) {
  return propertyName(propertyId)
}

function handleSelect(propertyId: Property, toggle: () => void) {
  if (props.file.hasProperty(propertyId)) {
    console.debug("Removing file property", props.file, propertyId)
    const value = props.file.property(propertyId)!.value
    updateFileProperty(props.file, propertyId, value, true)
  }
  else {
    console.debug("Adding default file property", props.file, propertyId)
    createPropertyWithDefaultValue(props.file, propertyId)
  }
  toggle()
}
</script>

<template>
  <dropdown
    anchor-x="right"
    anchor-y="bottom"
    flow-x="left"
    flow-y="down"
  >
    <template #default="{ toggle }">
      <panel-button @click="toggle()">
        <template #icon>
          <icon-property
            size="xs"
            filled
            type="header"
          />
          Add / Remove
        </template>
      </panel-button>
    </template>
    <template #menu="{ toggle }">
      <dropdown-menu class="dropdown-menu">
        <div class="inline-select-dropdown">
          <div
            v-for="option in options"
            :key="option"
            class="inline-select-option group"
            @click="handleSelect(option, toggle)"
          >
            <div>{{ label(option) }}</div>
            <icon-action
              v-if="file.hasProperty(option)"
              size="xs"
              type="check"
              class="icon check"
            />
          </div>
        </div>
      </dropdown-menu>
    </template>
  </dropdown>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.dropdown-menu
  @apply my-2 min-w-[200px]

.inline-select-dropdown
  @apply flex flex-col gap-[#{$menu-gap-items}] p-[#{$menu-p-default}]

.inline-select-option
  @apply flex items-center
  @apply h-5 -m-1 p-1 gap-2
  @apply t-menu-sm text-[#{$global-t-default}]
  @apply rounded-[#{$menu-br-item}]
  &:hover
    @apply cursor-pointer
    @apply bg-[#{$menu-bg-item-hover}]
  .icon
    @apply ml-auto h-4 w-4
    @apply text-[#{$global-bg-icon-secondary}]
</style>
